
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { DownloadIcon, SparklesIcon } from '@heroicons/react/outline';
import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import { Image as VideoType } from 'types';
import { getImageBySlug, getPublicVideosPaginated } from '@/data/images';
import { CopyBtn } from '@/components/atoms/CopyBtn';
import Button from '@/components/buttons/Button';
import CallToAction from '@/components/CallToAction';
import NewFooter from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import MediaSpecs from '@/components/media/MediaSpecs';
import Seo from '@/components/Seo';
import UserVideos from '@/components/videos/UserVideos';
import { getOptimizedImageUrl } from '@/utils/useOptimizedImage';
interface VideoProps {
    video: VideoType;
    videos: VideoType[];
    isStaticPage?: boolean;
}
const VideoPage = ({ video, videos, isStaticPage = true }: VideoProps) => {
    const [showSticky, setShowSticky] = useState(false);
    useEffect(() => {
        const handleScroll = () => {
            // Show sticky section after scrolling 2 viewport heights
            const scrollThreshold = window.innerHeight * 1.3;
            setShowSticky(window.scrollY > scrollThreshold);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    if (!video) {
        return null;
    }
    return (<main className='relative bg-gray-100 dark:bg-slate-900'>
      <Seo templateTitle={video.title + ' | AI Video Generator'} description={video.description + ' Generated by AI.'} image={getOptimizedImageUrl(video.image_url, 'medium')}/>
      <LandingPageNavigation skipUserContext/>
      <section>
        <div className='relative isolate'>
          <svg className='absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)] dark:stroke-gray-800' aria-hidden='true'>
            <defs>
              <pattern id='1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84' width={200} height={200} x='50%' y={-1} patternUnits='userSpaceOnUse'>
                <path d='M.5 200V.5H200' fill='none'/>
              </pattern>
            </defs>
            <svg x='50%' y={-1} className='overflow-visible fill-gray-50 dark:hidden'>
              <path d='M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z' strokeWidth={0}/>
            </svg>
            <rect width='100%' height='100%' strokeWidth={0} fill='url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)'/>
          </svg>
          <div className='absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48' aria-hidden='true'>
            <div className='aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30' style={{
            clipPath: 'polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)',
        }}/>
          </div>
          <div className='overflow-hidden'>
            <div className='mx-auto max-w-7xl px-6 pb-6 pt-8 sm:pt-12 lg:px-8 lg:pt-16 xl:px-3'>
              <div className='mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center'>
                <div className='flex justify-end gap-8 sm:justify-start lg:mt-0 lg:pl-0 xl:w-1/2 xl:shrink-0'>
                  <script type='application/ld+json'>
                    {`
                        {
                            "@context": "https://schema.org/",
                            "@type": "VideoObject",
                            "contentUrl": "${video.image_url}",
                            "license": "https://creativecommons.org/licenses/by-nd/4.0/deed.en",
                            "creditText": "Easy-Peasy.AI - AI Video Generator",
                            "creator": {
                                "@type": "Company",
                                "name": "Easy-Peasy.AI"
                            },
                            "copyrightNotice": "Easy-Peasy.AI - AI Video Generator"
                        }
                    `}
                  </script>
                  <div className='w-full'>
                    <video src={video.image_url} controls className='rounded-md w-full h-auto' preload='metadata' playsInline autoPlay muted loop/>
                  </div>
                </div>
                <div className='mt-10 w-full max-w-xl lg:mt-0  xl:max-w-2xl'>
                  <h1 className='text-2xl mb-5 font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-3xl'>
                    {video.h1}
                  </h1>
                  <div className='w-full sm:w-auto'>
                    <ButtonLink href="/ai-videos" variant='dark' onClick={() => {
            localStorage.setItem('savedVideoPrompt', video.prompt);
        }}>
                      Create videos like this &rarr;
                    </ButtonLink>
                  </div>
                  <div className='mt-5 flex items-center '>
                    <h2 className='text-lg font-bold tracking-tight text-gray-700 dark:text-slate-400 sm:text-xl'>
                      Prompt
                    </h2>
                    <CopyBtn value={video.prompt} variant='plain' showText={false}/>
                  </div>
                  <div className='relative mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800'>
                    <p className='text-base leading-relaxed text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words'>
                      {video.prompt}
                    </p>
                  </div>
                  <div className='mt-6 space-y-3'>
                    {video.user_prompt &&
            !video.prompt
                .toLowerCase()
                .includes(video.user_prompt.toLowerCase().trim()) && (<div className='text-sm text-gray-600'>
                          <span className='font-semibold text-gray-800'>
                            Original Prompt:
                          </span>{' '}
                          {video.user_prompt}
                        </div>)}
                    <div className='text-sm text-gray-600'>
                      <span className='font-semibold text-gray-800'>
                        Model:
                      </span>{' '}
                      {video.model || 'Hunyuan Video'}
                    </div>
                    <div className='text-sm text-gray-600'>
                      <span className='font-semibold text-gray-800'>
                        Created on
                      </span>{' '}
                      {video?.created_at &&
            new Intl.DateTimeFormat('en-US').format(new Date(video?.created_at))}{' '}
                      <a href={`mailto:support@easy-peasy.ai?subject=Report Video&body=Video Link: https://easy-peasy.ai/ai-video-generator/videos/${video.slug}`} className='ml-3 text-sm underline hover:no-underline hover:text-red-600 transition-colors' target='_blank' rel='nofollow noreferrer noopener'>
                        Report
                      </a>
                    </div>
                    {video?.updated_at &&
            video?.created_at &&
            new Date(video.updated_at).toDateString() !==
                new Date(video.created_at).toDateString() && (<div className='text-sm text-gray-600'>
                          <span className='font-semibold text-gray-800'>
                            Updated on
                          </span>{' '}
                          {new Intl.DateTimeFormat('en-US').format(new Date(video.updated_at))}{' '}
                        </div>)}
                  </div>

                  <MediaSpecs url={video.image_url} isVideo request={video.request} usedCredits={video.used_credits}/>

                  <div className='mt-6'>
                    <span className='text-xs text-gray-500'>
                      <a href='https://creativecommons.org/licenses/by/4.0/deed.en' className='text-xs text-gray-500 underline hover:no-underline' target='_blank' rel='nofollow noreferrer noopener'>
                        License
                      </a>
                      : Free to use with a backlink to Easy-Peasy.AI
                    </span>
                  </div>

                  <div className='mt-10 flex flex-wrap '>
                    <div className='w-full space-x-3 sm:w-auto'>
                      <CopyBtn value={`https://easy-peasy.ai/ai-video-generator/videos/${video.slug}`} text='Copy URL'/>

                      <Button variant='light' onClick={() => window.open(`${video.image_url}`)}>
                        <DownloadIcon className='mr-1 h-4 w-4 '/>
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <UserVideos initialVideos={videos} currentVideo='0'/>

      {/* Sticky CTA Section */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-in-out ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className='border-t border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95'>
          <div className='mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8'>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-between'>
              <div className='hidden sm:block'>
                <video src={video.image_url} className='rounded-lg object-cover' width={88} height={88} muted/>
              </div>
              <div className='flex-1 text-center sm:mx-6 sm:text-left'>
                <h2 className='text-lg font-semibold text-gray-900 dark:text-slate-200 sm:text-xl'>
                  Create Your Own AI Videos
                </h2>
                <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
                  Generate stunning AI videos with our easy-to-use video
                  generator
                </p>
              </div>
              <div className='w-full flex-shrink-0 sm:w-auto'>
                <Button onClick={() => {
            localStorage.setItem('savedVideoPrompt', video.prompt);
            window.open(`/ai-videos`, '_self');
        }} variant='dark' className='w-full justify-center sm:w-auto'>
                  <SparklesIcon className='mr-2 h-5 w-5'/>
                  Start Creating Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CallToAction preset='ai-videos'/>
      <NewFooter />
    </main>);
};
export default VideoPage;
const getServerSideProps: GetServerSideProps = async (ctx) => {
    const video = await getImageBySlug(ctx?.params?.uuid as string);
    const locale = ctx.locale || 'en';
    //redirect 301
    if (locale !== 'en') {
        return {
            redirect: {
                destination: `/ai-video-generator/videos/${video.slug}`,
                permanent: true,
            },
        };
    }
    if (!video || !video.is_public || !video.is_video) {
        return {
            notFound: true,
        };
    }
    const videos = await getPublicVideosPaginated(1, 24);
    const filteredVideos = videos.filter((v) => v.slug !== video.slug);
    return {
        props: {
            video: {
                created_at: video.created_at,
                updated_at: video.updated_at,
                id: video.id,
                image_url: video.image_url,
                //@ts-ignore
                prompt: 
                //@ts-ignore
                (video.response_data && video.response_data[0]?.revised_prompt) ||
                    video.prompt,
                user_prompt: video.user_prompt,
                used_credits: video.used_credits,
                request: video.request || null,
                title: video.title_new || video.title,
                description: video.description,
                h1: video.h1,
                slug: video.slug,
                model: video.model,
            },
            videos: filteredVideos,
        },
    };
};

    async function __Next_Translate__getServerSideProps__1a0cbfb655e__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/videos/[uuid]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a0cbfb655e__ as getServerSideProps }
  